/**
 * Message the studio's behavior half (web-studio-sites D15): the heading and
 * line, and the one action. Signed in, it opens the page's messages panel. A
 * visitor gets Telegram's sign-in window first, the same sign-in as the
 * header's, and the panel opens once they are signed in; closing the window
 * leaves the page as it was. Draws nothing.
 */
import { useQueryClient } from '@tanstack/react-query';
import type { SiteSessionDto } from '@telify/shared';
import { useEffect, useMemo, useState } from 'react';

import { sessionKeys, useTelegramSignIn, useWebSession } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { readPluginOption } from '../../site';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useMessagesPanel } from '../../messages/panelContext';
import { contract, type MessageStudioActions, type MessageStudioEvents, type MessageStudioState } from './contract';

export type MessageStudioRuntime = PieceRuntime<typeof contract, MessageStudioState, MessageStudioActions>;

export interface MessageStudioInput extends PieceInput<MessageStudioEvents> {
  /** On Telify's service and event pages: what the questions are about. */
  readonly about?: 'service' | 'event';
}

export function useMessageStudio(input: MessageStudioInput = {}): MessageStudioRuntime {
  const options = usePieceOptions(contract, input.options);
  const queryClient = useQueryClient();
  const studio = useSiteStudio().data;
  const signedIn = useWebSession().status === 'signedIn';
  const telegram = useTelegramSignIn();
  const panel = useMessagesPanel();
  // Pressed by a visitor: open the panel once Telegram's sign-in comes back signed in.
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    if (!waiting || telegram.state === 'pending') return;
    setWaiting(false);
    // The sign-in records the session before it settles; the session's readers hear of it a moment later.
    if (signedIn || queryClient.getQueryData<SiteSessionDto>(sessionKeys.session)?.signedIn) panel.openMessages();
  }, [waiting, telegram.state, signedIn, queryClient, panel]);

  const actions = useMemo<MessageStudioActions>(
    () => ({
      messageStudio: () => {
        if (signedIn) {
          panel.openMessages();
          return;
        }
        setWaiting(true);
        telegram.signInOneTap();
      },
    }),
    [signedIn, panel, telegram],
  );

  const heading = options.heading ?? (input.about ? `Questions about this ${input.about}?` : 'Questions before you book?');
  // The site's own line when it sets one (booking's messageStudioLine), {studio} filled with the studio's name.
  const line = String(readPluginOption('booking', 'messageStudioLine'));
  return {
    state: {
      status: studio ? 'ready' : 'loading',
      heading,
      line: studio ? line.split('{studio}').join(studio.name) : '',
      logoUrl: studio?.logoUrl ?? null,
      ready: signedIn || telegram.oneTapReady,
      signingIn: waiting,
      signInFailed: !signedIn && telegram.state === 'error',
    },
    actions,
    options,
  };
}
